import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import { faqData } from '@/mocks/faq';
import { externalLinks, siteSettings } from '@/mocks/siteSettings';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function RevealWrap({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useScrollReveal(0.06);
  return (
    <div ref={ref} className={`section-reveal ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

const CANONICAL = 'https://fuko.sonbook.net/faq';

function buildFaqJsonLd() {
  const mainEntity: Record<string, unknown>[] = [];
  faqData.forEach((cat) => {
    cat.items.forEach((item) => {
      const answerText = item.a.replace(/\n\n/g, ' ');
      mainEntity.push({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText,
        },
      });
    });
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${CANONICAL}#webpage`,
    url: CANONICAL,
    name: '複刻文化常見問題',
    inLanguage: 'zh-TW',
    isPartOf: { '@id': 'https://fuko.sonbook.net/#website' },
    breadcrumb: { '@id': `${CANONICAL}#breadcrumb` },
    mainEntity,
  };
}

function buildBreadcrumbJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${CANONICAL}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首頁', item: 'https://fuko.sonbook.net/' },
      { '@type': 'ListItem', position: 2, name: '常見問題', item: CANONICAL },
    ],
  };
}

function renderAnswer(text: string) {
  const paragraphs = text.split('\n\n');
  return paragraphs.map((p, i) => (
    <p key={i} className={i > 0 ? 'mt-3' : ''}>{p}</p>
  ));
}

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<{ category: number; item: number } | null>(null);

  const faqPageJsonLd = useMemo(() => buildFaqJsonLd(), []);
  const breadcrumbJsonLd = useMemo(() => buildBreadcrumbJsonLd(), []);

  const toggleItem = (catIdx: number, itemIdx: number) => {
    if (openIndex?.category === catIdx && openIndex?.item === itemIdx) {
      setOpenIndex(null);
    } else {
      setOpenIndex({ category: catIdx, item: itemIdx });
    }
  };

  return (
    <>
      <PageMeta
        title="複刻文化 Fuko 常見問題｜經典書籍與購書資訊"
        description="查看複刻文化常見問題，了解人文歷史、經典文本、公版經典與知識再詮釋，以及正式書目、購書通路和品牌合作資訊。"
        canonical={CANONICAL}
        ogTitle="複刻文化常見問題｜經典文本、書目與購書資訊"
        ogDescription="了解複刻文化的人文歷史與經典出版定位，以及正式書目、合作購書通路、價格庫存與品牌合作資訊。"
        jsonLd={[faqPageJsonLd, breadcrumbJsonLd]}
      />
      <Navbar />
      <main>

        {/* §1 · Hero */}
        <section className="relative w-full flex items-center justify-center paper-texture" style={{ minHeight: '100dvh', backgroundColor: 'oklch(var(--background-100))' }}>
          <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=A%20single%20open%20antique%20book%20with%20cream-colored%20deckle-edged%20pages%20resting%20on%20a%20natural%20linen%20surface%20beside%20a%20pair%20of%20vintage%20round%20reading%20glasses%20with%20thin%20gold%20wire%20frames%2C%20soft%20morning%20light%20from%20a%20window%20creating%20a%20gentle%20diagonal%20illumination%20across%20the%20open%20pages%20and%20casting%20very%20subtle%20shadows%2C%20the%20book%20pages%20show%20faint%20printed%20text%20in%20both%20Chinese%20vertical%20columns%20and%20Western%20serif%20type%20slightly%20blurred%20as%20the%20focus%20is%20on%20the%20texture%20and%20atmosphere%20rather%20than%20legibility%2C%20a%20simple%20celadon%20porcelain%20cup%20of%20tea%20sits%20quietly%20in%20the%20background%20out%20of%20focus%20next%20to%20a%20small%20brass%20magnifying%20glass%2C%20modern%20minimalist%20editorial%20still%20life%20photography%20blending%20Eastern%20and%20Western%20antiquarian%20aesthetics%2C%20intellectual%20contemplative%20atmosphere%2C%20refined%20restrained%20mood%20with%20peaceful%20quiet%20aesthetic%2C%20warm%20neutral%20color%20palette%20of%20cream%20eggshell%20warm%20gray%20and%20subtle%20beige%2C%20sophisticated%20cultural%20publishing%20visual%20identity%2C%20generous%20negative%20space%20around%20the%20central%20subject%2C%20editorial%20magazine%20quality%2C%20scholarly%20library%20still%20life&width=1600&height=900&seq=fuko-faq-hero-vintage-01&orientation=landscape&nocache=true" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.30 }} />
            <span className="absolute top-[10%] right-[7%] font-display italic font-bold select-none hidden xl:block" style={{ fontSize: 180, color: 'oklch(var(--foreground-950)/0.014)' }}>?</span>
            <span className="absolute bottom-[6%] left-[5%] font-display italic text-[10px] hidden lg:block" style={{ color: 'oklch(var(--foreground-400)/0.15)' }}>F.001</span>
          </div>

          <RevealWrap className="relative w-full">
            <div className="w-full px-6 md:px-12 lg:px-16 pt-20 md:pt-24 pb-20 md:pb-28" style={{ zIndex: 1 }}>
              <div className="max-w-[min(100%-32px,800px)] mx-auto text-center">
                <div className="flex items-center justify-center gap-3 mb-4" style={{ color: 'oklch(var(--foreground-400)/0.42)' }}>
                  <span className="font-display italic text-[13px]">F.001</span>
                  <span className="w-6 h-px" style={{ backgroundColor: 'oklch(var(--foreground-300)/0.20)' }} />
                  <span className="font-label text-[11px] tracking-[0.2em] uppercase">FAQ</span>
                </div>

                <h1 className="font-heading font-bold text-foreground-900 leading-[1.15] mb-2" style={{ fontSize: 'clamp(2rem,4vw,3.5rem)', letterSpacing: '-0.01em' }}>
                  複刻文化常見問題
                </h1>
                <p className="font-display italic font-bold mb-6" style={{ fontSize: 'clamp(0.9rem,1.3vw,1.1rem)', color: 'oklch(var(--primary-500))' }}>
                  Frequently Asked Questions
                </p>

                <div className="text-foreground-600 leading-[1.8] max-w-lg mx-auto space-y-3" style={{ fontSize: 'clamp(0.875rem,1.05vw,0.95rem)' }}>
                  <p>本頁整理複刻文化常見問題，內容包含品牌定位、人文歷史、經典文本、公版經典、正式書目查詢、合作購書通路、價格與庫存，以及品牌經銷與合作資訊。</p>
                  <p>複刻文化聚焦人文歷史、經典文本、公版經典與知識再詮釋，透過重新整理與當代閱讀視角，讓跨越時代的作品重新被理解與閱讀。</p>
                  <p>複刻文化為崧博出版合作經銷品牌之一。完整書名、作者、ISBN與出版日期請以正式書目資料為準；價格、版本、庫存及購買方式則以各合作通路頁面為準。</p>
                </div>
              </div>
            </div>
          </RevealWrap>
        </section>

        <div className="ornament-divider my-10 md:my-14" aria-hidden="true">
          <div className="ornament-divider-icon font-display italic">&#10087;</div>
        </div>

        {/* §2 · FAQ Accordion */}
        <section className="relative w-full" style={{ minHeight: '100dvh', backgroundColor: 'oklch(var(--background-50))' }}>
          <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
            <span className="absolute bottom-10 right-[8%] font-display italic font-bold select-none hidden lg:block" style={{ fontSize: 110, color: 'oklch(var(--foreground-950)/0.011)' }}>FAQ</span>
          </div>

          <RevealWrap className="relative w-full">
            <div className="w-full px-6 md:px-12 lg:px-16 py-20 md:py-28">
              <div className="max-w-[min(100%-32px,800px)] mx-auto">

                <div className="mb-16 text-sm text-foreground-600 leading-[1.8] space-y-2">
                  <p>複刻文化常見問題頁整理品牌定位、人文歷史、經典文本、公版經典、知識再詮釋、正式書目查詢、合作購書通路、價格庫存及品牌合作等資訊。</p>
                  <p>複刻文化所指的「複刻」，是閱讀與編輯層面的重新整理、重新理解與重新呈現，不代表古物仿製、文物修復或收藏品販售。</p>
                  <p>複刻文化為崧博出版合作經銷品牌之一。完整書名、作者、ISBN與出版日期請以崧博出版書籍資料庫為準；價格、版本、庫存及購買方式則以各合作購書通路頁面為準。</p>
                </div>

                <div className="space-y-16">
                  {faqData.map((category, catIdx) => (
                    <div key={category.category}>
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 pb-4" style={{ borderBottom: '2px solid oklch(var(--foreground-200)/0.30)' }}>
                        {category.category}
                      </h2>

                      <div className="space-y-0">
                        {category.items.map((item, itemIdx) => {
                          const isOpen = openIndex?.category === catIdx && openIndex?.item === itemIdx;
                          const btnId = `faq-btn-${catIdx}-${itemIdx}`;
                          const panelId = `faq-panel-${catIdx}-${itemIdx}`;
                          return (
                            <div key={itemIdx} style={{ borderBottom: '1px solid oklch(var(--foreground-200)/0.15)' }}>
                              <h3 className="m-0">
                                <button
                                  id={btnId}
                                  onClick={() => toggleItem(catIdx, itemIdx)}
                                  className="w-full flex items-center justify-between py-5 text-left cursor-pointer group"
                                  aria-expanded={isOpen}
                                  aria-controls={panelId}
                                >
                                  <span className={`font-medium pr-8 transition-colors ${isOpen ? 'text-foreground-900' : 'text-foreground-700 group-hover:text-foreground-900'}`} style={{ fontSize: 'clamp(0.875rem,1vw,0.95rem)' }}>
                                    {item.q}
                                  </span>
                                  <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center transition-transform duration-300 ${isOpen ? 'rotate-45' : 'rotate-0'}`} aria-hidden="true">
                                    <i className="ri-add-line" style={{ color: isOpen ? 'oklch(var(--primary-500))' : 'oklch(var(--foreground-400)/0.50)' }} />
                                  </span>
                                </button>
                              </h3>

                              <div
                                id={panelId}
                                role="region"
                                aria-labelledby={btnId}
                                className="overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none"
                                style={{
                                  maxHeight: isOpen ? '800px' : '0',
                                  opacity: isOpen ? 1 : 0,
                                }}
                              >
                                <div className="pb-6 pr-8">
                                  <div className="text-sm md:text-[15px] text-foreground-600 leading-[1.8]">
                                    {renderAnswer(item.a)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* § 查詢複刻文化正式書目 */}
                <div className="mt-16 pt-12" style={{ borderTop: '2px solid oklch(var(--foreground-200)/0.20)' }}>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">
                    查詢複刻文化正式書目
                  </h2>
                  <p className="text-sm text-foreground-600 leading-[1.8] mb-6">
                    如需確認複刻文化出版作品的完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢。
                  </p>
                  <a
                    href={externalLinks.catalog}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                    style={{ padding: '11px 24px', backgroundColor: 'oklch(var(--primary-500))', color: 'oklch(var(--background-50))' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-600))')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-500))')}
                  >
                    查詢完整書目 <i className="ri-external-link-line text-xs" aria-hidden="true" />
                  </a>
                </div>

                {/* § 查看複刻文化合作購書通路 */}
                <div className="mt-12 pt-12" style={{ borderTop: '2px solid oklch(var(--foreground-200)/0.20)' }}>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">
                    查看複刻文化合作購書通路
                  </h2>
                  <p className="text-sm text-foreground-600 leading-[1.8] mb-6">
                    複刻文化相關書籍可透過網站已建立的合作購書通路查找。價格、版本、庫存、配送及購買方式請以各通路頁面為準。
                  </p>
                  <Link
                    to="/stores"
                    className="inline-flex items-center justify-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                    style={{ padding: '11px 24px', border: '1px solid oklch(var(--foreground-300)/0.22)', color: 'oklch(var(--foreground-700))' }}
                    onMouseEnter={e => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.borderColor = 'oklch(var(--foreground-300)/0.40)';
                      el.style.backgroundColor = 'oklch(var(--background-100)/0.5)';
                    }}
                    onMouseLeave={e => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.borderColor = 'oklch(var(--foreground-300)/0.22)';
                      el.style.backgroundColor = 'transparent';
                    }}
                  >
                    查看合作購書通路 <i className="ri-arrow-right-line text-xs" aria-hidden="true" />
                  </Link>
                </div>

                {/* § 聯絡 */}
                <div className="mt-12 pt-12 text-center" style={{ borderTop: '2px solid oklch(var(--foreground-200)/0.20)' }}>
                  <p className="text-sm text-foreground-500 mb-6">未找到您需要的答案？</p>
                  <a
                    href={`mailto:${siteSettings.email}`}
                    className="inline-flex items-center justify-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                    style={{ padding: '11px 24px', border: '1px solid oklch(var(--foreground-300)/0.22)', color: 'oklch(var(--foreground-700))' }}
                    onMouseEnter={e => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.borderColor = 'oklch(var(--foreground-300)/0.40)';
                      el.style.backgroundColor = 'oklch(var(--background-100)/0.5)';
                    }}
                    onMouseLeave={e => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.borderColor = 'oklch(var(--foreground-300)/0.22)';
                      el.style.backgroundColor = 'transparent';
                    }}
                  >
                    聯絡我們 <i className="ri-arrow-right-line text-xs" aria-hidden="true" />
                  </a>
                </div>

              </div>
            </div>
          </RevealWrap>
        </section>

      </main>
      <Footer />
    </>
  );
}
